import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'

import { Box, Checkbox, Chip, List, ListItem, TextField, Typography } from '@material-ui/core'
import { Autocomplete, AutocompleteChangeReason } from '@material-ui/lab'
import { debounce } from 'lodash'

import { withContext } from '../Context'
import { getSegmentValue } from '../segment-service'

import { CareerOption, onCareerSearch, useRelatedCareerSearch } from './career-service'
import SimilarCareer from './SimilarCareer'

type CareerSelectorProps = {
  criteria: string
  onChange: (criteria: string, segmentName: string, value: any, operation?: string) => void
  segment: object
}

export const SegmentName = 'Careers'

export const CareerSelector = ({ criteria = 'Must', onChange, segment }: CareerSelectorProps) => {
  const currentValue = getSegmentValue(segment, criteria, SegmentName)
  const [careers, setCareers] = useState<CareerOption[]>(currentValue)
  const [options, setOptions] = useState<CareerOption[]>([])
  const { relatedCareers } = useRelatedCareerSearch(careers)

  const debounced = debounce((callback) => {
    return callback()
  }, 300)

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setOptions([])
      return
    }
    debounced(() => {
      onCareerSearch(e.target.value).then((results) => {
        setOptions(results)
      })
    })
  }

  const onSelect = useCallback((_: ChangeEvent<unknown>, value: unknown, reason: AutocompleteChangeReason) => {
    if (reason === 'clear') {
      setCareers([])
      setOptions([])
    }
    setCareers(value as CareerOption[])
  }, [])

  const onAddSimilarCareer = useCallback(
    (similarCareer) => {
      const selectedCareers = [...careers]
      const newSelectedCareer = {
        SOC: similarCareer.SOC,
        Title: similarCareer.Title,
        name: similarCareer.Title,
        tokens: [similarCareer.Title],
        value: similarCareer.SOC,
      }
      selectedCareers.push(newSelectedCareer)
      setCareers(selectedCareers)
    },
    [careers],
  )

  useEffect(() => {
    const careerValues = careers.map((career) => {
      const { name } = career as unknown as CareerOption
      if (name === undefined) {
        return career as unknown as string
      }

      return name
    })
    onChange(criteria, SegmentName, careerValues)
    // Only update on career change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careers])

  return (
    <>
      <Box py={2}>
        <Autocomplete
          multiple
          defaultValue={careers}
          value={careers}
          options={options}
          disableCloseOnSelect
          aria-label="Search for a career"
          noOptionsText="Type to search..."
          onChange={onSelect}
          getOptionSelected={(option, value) => {
            const optionName = option.name || option
            const valueName = value.name || value

            return optionName === valueName
          }}
          getOptionLabel={(option) => {
            const { name } = option as unknown as CareerOption
            if (name === undefined) {
              return option as unknown as string
            }            
            return name
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Career Interests"
              label="Career Interests"
              onChange={handleInputChange}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off',
              }}
            />
          )}
          renderTags={(value, getTagProps: ({ index }: { index: number }) => object) => (
            <Box
              maxHeight="120px"
              overflow="auto"
            >
              {value.map((option, index) => {
                const name = option?.name || option
                return (
                  <Chip
                    key={index}
                    color="primary"
                    label={name}
                    {...getTagProps({ index })}
                  />
                )
              })}
            </Box>
          )}
          renderOption={(option, state: { selected: boolean; inputValue: string }) => {
            const { name } = option as unknown as CareerOption
            return (
              <React.Fragment>
                <Checkbox
                  edge="start"
                  color="primary"
                  checked={state.selected}
                  disableRipple
                  inputProps={{ 'aria-labelledby': name }}
                />
                <Box marginLeft={2}>
                  <Typography>{name}</Typography>
                </Box>
              </React.Fragment>
            )
          }}
        />
      </Box>
      {careers.length && relatedCareers.length ? (
        <List>
          <ListItem disableGutters>
            <Typography variant="subtitle2">Add Similar Careers</Typography>
          </ListItem>
          {relatedCareers?.map((career) => (
            <SimilarCareer
              relatedOccupation={career}
              onAdd={onAddSimilarCareer}
            />
          ))}
        </List>
      ) : null}
    </>
  )
}

export default withContext(CareerSelector)
